/**
 * Pathfinding utilities for location system.
 *
 * Implements Dijkstra's algorithm for finding shortest paths
 * between locations in the game world graph.
 */

import type { LocationDefinition, TravelRoute } from '@idle-depths/shared';

/**
 * Calculate the shortest path between two locations using Dijkstra's algorithm.
 *
 * The algorithm:
 * 1. Builds an adjacency graph from location connections
 * 2. Finds the shortest path by travel time (not number of hops)
 * 3. Returns the full route including intermediate locations
 *
 * @param locations - All available location definitions
 * @param fromId - Starting location ID
 * @param toId - Destination location ID
 * @returns The shortest route, or null if no path exists
 */
export function calculateRoute(
  locations: LocationDefinition[],
  fromId: string,
  toId: string
): TravelRoute | null {
  // Same location - no travel needed
  if (fromId === toId) {
    return null;
  }

  // Build adjacency map (only include unlocked connections)
  const graph = new Map<string, Array<{ toLocationId: string; travelTimeMs: number }>>();
  for (const location of locations) {
    graph.set(
      location.id,
      location.connections.filter((connection) => connection.isUnlocked !== false)
    );
  }

  // Verify start and end locations exist in graph
  if (!graph.has(fromId) || !graph.has(toId)) {
    return null;
  }

  // Initialize Dijkstra's algorithm
  const distances = new Map<string, number>();
  const previous = new Map<string, string>();
  const unvisited = new Set<string>(locations.map((location) => location.id));

  // Starting point has distance 0
  distances.set(fromId, 0);

  // Process nodes until we reach destination or exhaust all reachable nodes
  while (unvisited.size > 0) {
    // Find unvisited node with smallest distance
    let currentNode: string | null = null;
    let minimumDistance = Infinity;

    for (const node of unvisited) {
      const distance = distances.get(node) ?? Infinity;
      if (distance < minimumDistance) {
        minimumDistance = distance;
        currentNode = node;
      }
    }

    // No more reachable nodes (disconnected graph)
    if (!currentNode || minimumDistance === Infinity) {
      break;
    }

    // Reached destination - we can stop
    if (currentNode === toId) {
      break;
    }

    // Mark as visited
    unvisited.delete(currentNode);

    // Update distances to neighbors
    const connections = graph.get(currentNode) ?? [];
    for (const connection of connections) {
      const alternativeDistance =
        (distances.get(currentNode) ?? Infinity) + connection.travelTimeMs;

      if (alternativeDistance < (distances.get(connection.toLocationId) ?? Infinity)) {
        distances.set(connection.toLocationId, alternativeDistance);
        previous.set(connection.toLocationId, currentNode);
      }
    }
  }

  // Check if destination was reached
  const totalDistance = distances.get(toId);
  if (totalDistance === undefined || totalDistance === Infinity) {
    return null; // No path exists
  }

  // Reconstruct path from destination to start
  const path: string[] = [];
  let current: string | undefined = toId;

  while (current) {
    path.unshift(current);
    const previousNode = previous.get(current);
    if (!previousNode) {
      break;
    }
    current = previousNode;
  }

  // Calculate travel time for each leg
  const legTimesMs: number[] = [];
  for (let index = 0; index < path.length - 1; index++) {
    const fromLocation = path[index];
    const toLocation = path[index + 1];
    if (!fromLocation || !toLocation) continue;
    const connection = graph.get(fromLocation)?.find((conn) => conn.toLocationId === toLocation);
    legTimesMs.push(connection?.travelTimeMs ?? 0);
  }

  return {
    fromLocationId: fromId,
    toLocationId: toId,
    path,
    totalTimeMs: totalDistance,
    legTimesMs,
  };
}

/**
 * Calculate cumulative times for each leg in a route.
 *
 * Used to determine which leg the player is on based on elapsed time.
 *
 * @param route - The travel route
 * @returns Array of cumulative end times for each leg
 */
export function calculateCumulativeLegTimes(route: TravelRoute): number[] {
  const cumulativeTimes: number[] = [];
  let totalTime = 0;

  for (const legTime of route.legTimesMs) {
    totalTime += legTime;
    cumulativeTimes.push(totalTime);
  }

  return cumulativeTimes;
}

/**
 * Find which leg of the journey the player is on based on elapsed time.
 *
 * @param route - The travel route
 * @param elapsedMs - Time elapsed since travel started
 * @returns Object with current leg index and progress through that leg
 */
export function findCurrentLeg(
  route: TravelRoute,
  elapsedMs: number
): { legIndex: number; legProgress: number } {
  let legStartTime = 0;

  for (let index = 0; index < route.legTimesMs.length; index++) {
    const legTime = route.legTimesMs[index] ?? 0;
    const legEndTime = legStartTime + legTime;

    if (elapsedMs < legEndTime) {
      // Currently in this leg
      const legProgress = legTime > 0 ? (elapsedMs - legStartTime) / legTime : 1;
      return {
        legIndex: index,
        legProgress: Math.min(1, Math.max(0, legProgress)),
      };
    }

    legStartTime = legEndTime;
  }

  // Travel complete - return last leg at 100%
  return {
    legIndex: route.legTimesMs.length - 1,
    legProgress: 1,
  };
}

/**
 * Find the location the player has fully reached based on elapsed time.
 *
 * Used for offline progress to determine where to place the character.
 *
 * @param route - The travel route
 * @param elapsedMs - Time elapsed since travel started
 * @returns The location ID the player has fully reached
 */
export function findFarthestCompletedLocation(route: TravelRoute, elapsedMs: number): string {
  let legStartTime = 0;

  for (let index = 0; index < route.legTimesMs.length; index++) {
    const legTime = route.legTimesMs[index] ?? 0;
    const legEndTime = legStartTime + legTime;

    if (elapsedMs < legEndTime) {
      // Haven't completed this leg - return start of this leg
      return route.path[index] ?? route.fromLocationId;
    }

    legStartTime = legEndTime;
  }

  // All legs complete - return destination
  return route.toLocationId;
}
